import { createContext, useContext, useState } from "react";
import type { ReactNode } from "react";

interface AppHeaderContextValue {
  // Offers header state
  offersSearchQuery: string;
  setOffersSearchQuery: (query: string) => void;
  offersViewMode: "grid" | "list";
  setOffersViewMode: (mode: "grid" | "list") => void;
  offersSelectedCategory: string | null;
  setOffersSelectedCategory: (category: string | null) => void;
  offersSortBy: "discount" | "distance" | "popularity";
  setOffersSortBy: (sort: "discount" | "distance" | "popularity") => void;
  offersShowFilters: boolean;
  setOffersShowFilters: (show: boolean) => void;
  // Map header state
  mapTheme: string;
  setMapTheme: (theme: string) => void;
}

const AppHeaderContext = createContext<AppHeaderContextValue | null>(null);

export function AppHeaderProvider({ children }: { children: ReactNode }) {
  const [offersSearchQuery, setOffersSearchQuery] = useState("");
  const [offersViewMode, setOffersViewMode] = useState<"grid" | "list">("list");
  const [offersSelectedCategory, setOffersSelectedCategory] = useState<string | null>(null);
  const [offersSortBy, setOffersSortBy] = useState<"discount" | "distance" | "popularity">("discount");
  const [offersShowFilters, setOffersShowFilters] = useState(false);
  const [mapTheme, setMapTheme] = useState<string>(() => {
    const saved = localStorage.getItem("mapTheme");
    return saved === "default" || saved === "positron" ? saved : "default";
  });

  return (
    <AppHeaderContext.Provider
      value={{
        offersSearchQuery,
        setOffersSearchQuery,
        offersViewMode,
        setOffersViewMode,
        offersSelectedCategory,
        setOffersSelectedCategory,
        offersSortBy,
        setOffersSortBy,
        offersShowFilters,
        setOffersShowFilters,
        mapTheme,
        setMapTheme: (theme: string) => {
          setMapTheme(theme);
          localStorage.setItem("mapTheme", theme);
        },
      }}
    >
      {children}
    </AppHeaderContext.Provider>
  );
}

export function useAppHeader() {
  const context = useContext(AppHeaderContext);
  if (!context) {
    throw new Error("useAppHeader must be used within AppHeaderProvider");
  }
  return context;
}
